import moment from "moment";

import {
  PLAN_BUILDER_PREFILL_CREDITS_KEY,
  START_TOPUP_POOL_ALERT_PENDING_KEY,
  START_TOPUP_POOL_ALERT_SHOWN_KEY,
  USAGE_CREDITS_CATEGORY_MAP,
  USAGE_CREDITS_CATEGORY_META,
} from "./constants";

import { normalizeCustomerType } from "../../../utils/common";
import { hasPermission, permissions } from "../../../utils/Permissions";

import { designTokens } from "../../../styles/tokens";

export const formatCurrency = (amount) => (
  `$${Number(amount || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`
);

export const formatTestsChange = (value) => (value > 0 ? `+${value}` : `${value}`);

const ACTIVITY_LABELS = {
  topup: "Topup",
  deduct: "Deduct",
  refund: "Refund",
  grant: "Grant",
  adjustment: "Adjustment",
};

/**
 * Map free-text search into ledger query filters.
 * @param {string} query
 * @returns {{ search?: string }}
 */
export const resolveLedgerSearchParams = (query = "") => {
  const trimmed = String(query || "").trim();
  if (!trimmed) return {};
  return { search: trimmed };
};

/**
 * Build test-details text from API `lines` only.
 * @param {{ lines?: Array }} activity
 * @returns {string}
 */
export const formatLedgerTestDetails = (activity) => (
  (activity.lines || [])
    .map((line) => [line.usage_category_name, line.credits]
      .filter((value) => value != null && String(value).trim() !== "")
      .join(" "))
    .filter(Boolean)
    .join(" + ")
);

/**
 * Map a ledger activity row into Test Activity table shape (API fields only).
 * @param {Object} activity
 * @param {number} index
 * @returns {Object}
 */
export const mapLedgerActivityToRow = (activity, index = 0) => {
  const activityKey = String(activity.activity).toLowerCase();

  return {
    id: [
      activity.submission_id,
      activity.activity_date,
      activity.activity,
      activity.action_by,
      index,
    ].join("|"),
    date: moment(activity.activity_date).format("MMM D, YYYY"),
    activity: ACTIVITY_LABELS[activityKey],
    activityKey,
    testDetails: formatLedgerTestDetails(activity),
    submissionName: activity.submission_name,
    submissionId: activity.submission_id,
    tests: activity.credits_delta,
    balanceAfter: activity.balance_after,
    actionBy: activity.action_by,
    testItems: (activity.tests || []).map((item) => ({
      ...item,
      tests: item.credits,
    })),
  };
};

export const parseCreditAmount = (value) => {
  const amount = Number(value);
  if (Number.isNaN(amount)) {
    return 0;
  }
  return Math.floor(amount);
};

const getTestsLabelFromCredit = (credit) => {
  const label = credit?.tests_label ?? credit?.testsLabel;
  if (typeof label !== "string") {
    return undefined;
  }

  const trimmed = label.trim();
  return trimmed || undefined;
};

export const resolveUsageBillingMode = (customerType) => {
  const type = normalizeCustomerType(customerType);
  if (type === "subscription") return "subscription";
  if (type === "prepaid") return "prepaid";
  if (type === "postpay") return "postpay";
  return "normal";
};

/** Prepaid/subscription customers get credit billing; normal (incl. null) do not. */
export const isPrepaidOrSubscriptionCustomer = (customerType) => {
  const type = normalizeCustomerType(customerType);
  return type === "prepaid" || type === "subscription";
};

/** Usage Metrics tab: account show_usage_metrics and role view_usage_metrics. */
export const canViewUsageMetrics = () => (
  hasPermission(permissions.usageMetrics.showUsageMetrics)
  && hasPermission(permissions.subscriptions.viewUsageMetrics)
);

/** Invoice tab: account show_invoice and role view_invoice. */
export const canViewInvoice = () => (
  hasPermission(permissions.usageMetrics.showInvoice)
  && hasPermission(permissions.subscriptions.viewInvoice)
);

const getTierRemainingSubscription = (tier = {}) => parseCreditAmount(
  tier.remainingSubscriptionCredits ?? tier.remaining_subscription_credits ?? 0,
);

const getTierRemainingTopup = (tier = {}) => parseCreditAmount(
  tier.remainingTopUpCredits ?? tier.remaining_top_up_credits ?? 0,
);

const getTierSubscriptionUsed = (tier = {}) => parseCreditAmount(
  tier.subscriptionConsumedCredits ?? tier.subscription_consumed_credits ?? 0,
);

const getTierTopupUsed = (tier = {}) => parseCreditAmount(
  tier.topupConsumedCredits ?? tier.topup_consumed_credits ?? 0,
);

const getTierTotalUsed = (tier = {}) => (
  getTierSubscriptionUsed(tier) + getTierTopupUsed(tier)
);

const mapCreditRowToTierFields = (credit = {}) => {
  const remainingSubscriptionCredits = parseCreditAmount(
    credit.remaining_subscription_credits,
  );
  const remainingTopUpCredits = parseCreditAmount(
    credit.remaining_top_up_credits,
  );
  const subscriptionConsumedCredits = parseCreditAmount(
    credit.subscription_consumed_credits,
  );
  const topupConsumedCredits = parseCreditAmount(
    credit.topup_consumed_credits,
  );
  const totalCredits = parseCreditAmount(credit.total_credits);
  const subscriptionCredits = parseCreditAmount(credit.subscription_credits);
  const topUpCredits = parseCreditAmount(credit.top_up_credits);
  const totalUsed = subscriptionConsumedCredits + topupConsumedCredits;
  const hasConsumed = (
    credit.subscription_consumed_credits != null
    || credit.topup_consumed_credits != null
  );

  return {
    remainingSubscriptionCredits,
    remainingTopUpCredits,
    subscriptionConsumedCredits,
    topupConsumedCredits,
    topUpCredits: remainingTopUpCredits || topUpCredits,
    totalCredits,
    subscriptionCredits: remainingSubscriptionCredits || subscriptionCredits,
    ...(hasConsumed
      ? {
        used: totalUsed,
        total: totalUsed + totalCredits,
      }
      : {}),
  };
};

/**
 * Derives tier usage for category usage panels from API credits.
 * Subscription remaining = remaining_subscription_credits.
 * Subscription total = remaining_subscription_credits + subscription_consumed_credits.
 * Top-up remaining = remaining_top_up_credits.
 * Top-up total = remaining_top_up_credits + topup_consumed_credits.
 * billingMode "prepaid" → topup_consumed_credits / (topup_consumed_credits + remaining_top_up_credits).
 * Prepaid remaining = remaining_top_up_credits.
 * billingMode "postpay" consumed = topup_consumed_credits + subscription_consumed_credits.
 * Postpay adjustment = remaining_top_up_credits + remaining_subscription_credits.
 */
export const getSubscriptionTierUsage = (
  tier,
  {
    billingMode = "subscription",
    topupPlan = false,
  } = {},
) => {
  if (billingMode === "postpay") {
    const remaining = getTierRemainingSubscription(tier) + getTierRemainingTopup(tier);
    const used = getTierSubscriptionUsed(tier) + getTierTopupUsed(tier);
    const total = remaining + used;
    const percent = total > 0 ? Math.round((used / total) * 100) : 0;

    return {
      used,
      total,
      poolCredits: total,
      remaining,
      percent,
    };
  }

  const hasNewSubscriptionPool = (
    tier.remainingSubscriptionCredits != null
    || tier.remaining_subscription_credits != null
    || tier.subscriptionConsumedCredits != null
    || tier.subscription_consumed_credits != null
  );
  const hasNewTopupPool = (
    tier.remainingTopUpCredits != null
    || tier.remaining_top_up_credits != null
    || tier.topupConsumedCredits != null
    || tier.topup_consumed_credits != null
  );

  if (billingMode === "subscription" && !topupPlan && hasNewSubscriptionPool) {
    const remaining = getTierRemainingSubscription(tier);
    const used = getTierSubscriptionUsed(tier);
    const total = remaining + used;
    const percent = total > 0 ? Math.round((used / total) * 100) : 0;

    return {
      used,
      total,
      poolCredits: total,
      remaining,
      percent,
    };
  }

  if (billingMode === "subscription" && topupPlan && hasNewTopupPool) {
    const remaining = getTierRemainingTopup(tier);
    const used = getTierTopupUsed(tier);
    const total = remaining + used;
    const percent = total > 0 ? Math.round((used / total) * 100) : 0;

    return {
      used,
      total,
      poolCredits: total,
      remaining,
      percent,
    };
  }

  const subscriptionCredits = parseCreditAmount(
    tier.subscriptionCredits ?? tier.subscription_credits ?? 0,
  );
  const topUpCredits = parseCreditAmount(
    tier.topUpCredits ?? tier.top_up_credits ?? 0,
  );
  const hasConsumedTotals = (
    tier.subscriptionConsumedCredits != null
    || tier.subscription_consumed_credits != null
    || tier.topupConsumedCredits != null
    || tier.topup_consumed_credits != null
  );
  const hasSubscriptionPool = (
    tier.subscriptionCredits != null
    || tier.subscription_credits != null
  );
  const hasTopUpPool = (
    tier.topUpCredits != null
    || tier.top_up_credits != null
  );

  if (!topupPlan && billingMode !== "prepaid" && (hasConsumedTotals || hasSubscriptionPool)) {
    const poolCredits = subscriptionCredits;
    const used = getTierSubscriptionUsed(tier);
    const total = poolCredits;
    const remaining = Math.max(poolCredits - used, 0);
    const percent = total > 0 ? Math.round((used / total) * 100) : 0;

    return {
      used,
      total,
      poolCredits,
      remaining,
      percent,
    };
  }

  if (topupPlan && billingMode !== "prepaid" && (hasConsumedTotals || hasTopUpPool)) {
    const poolCredits = topUpCredits;
    const used = getTierTopupUsed(tier);
    const total = poolCredits;
    const remaining = Math.max(poolCredits - used, 0);
    const percent = total > 0 ? Math.round((used / total) * 100) : 0;

    return {
      used,
      total,
      poolCredits,
      remaining,
      percent,
    };
  }

  if (billingMode === "prepaid") {
    const remaining = getTierRemainingTopup(tier);
    const used = getTierTopupUsed(tier);
    const total = remaining + used;
    const percent = total > 0 ? Math.round((used / total) * 100) : 0;

    return {
      used,
      total,
      poolCredits: total,
      remaining,
      percent,
    };
  }

  const total = parseCreditAmount(tier.totalCredits);
  const used = parseCreditAmount(
    topupPlan ? tier.topUpCredits : tier.subscriptionCredits,
  );
  const remaining = Math.max(total - used, 0);
  const percent = total > 0 ? Math.round((used / total) * 100) : 0;

  return {
    used,
    total,
    remaining,
    percent,
  };
};

/**
 * Map ledger activities[] into Test Activity table rows.
 * @param {Array} activities
 * @returns {Array}
 */
export const mapLedgerActivitiesToRows = (activities) => (
  activities.map(mapLedgerActivityToRow)
);

/* Maps /usage/credits/ response into category usage UI shape.
 * Progress fields are derived later via getSubscriptionTierUsage({ billingMode, topupPlan }).
 */
export const mapUsageCreditsToCategoryUsage = (
  creditsResponse,
  {
    categoryMap,
    categoryMeta,
  } = {},
) => {
  const credits = creditsResponse.result.credits;
  if (!credits.length) {
    return [];
  }

  const tiersByCategory = {};
  const testsLabelByCategory = {};

  credits.forEach((credit) => {
    const mapping = categoryMap[credit.usage_category_code];
    if (!mapping) {
      return;
    }

    const testsLabel = getTestsLabelFromCredit(credit);
    if (testsLabel && !testsLabelByCategory[mapping.categoryKey]) {
      testsLabelByCategory[mapping.categoryKey] = testsLabel;
    }

    const tierFields = mapCreditRowToTierFields(credit);

    if (!tiersByCategory[mapping.categoryKey]) {
      tiersByCategory[mapping.categoryKey] = {};
    }

    tiersByCategory[mapping.categoryKey][mapping.tierName] = {
      name: mapping.tierName,
      code: credit.usage_category_code,
      categoryName: credit.usage_category_name,
      ...tierFields,
    };
  });

  return Object.keys(categoryMeta)
    .map((categoryKey) => {
      const meta = categoryMeta[categoryKey];
      const categoryTiers = tiersByCategory[categoryKey] || {};
      const tiers = meta.tierOrder
        .map((tierName) => categoryTiers[tierName])
        .filter(Boolean);

      if (!tiers.length) {
        return null;
      }

      return {
        key: meta.key,
        name: meta.name,
        ...(testsLabelByCategory[categoryKey]
          ? { testsLabel: testsLabelByCategory[categoryKey] }
          : {}),
        tiers,
      };
    })
    .filter(Boolean);
};

const PLAN_USAGE_SHORT_TITLES = {
  microbiology: "Micro",
  chemistry: "Chemi",
  environment: "Env",
};

const getTierPlanUsageUsed = (tier, poolType = "subscription") => {
  if (poolType === "topup") {
    if (
      tier.topupConsumedCredits != null
      || tier.topup_consumed_credits != null
    ) {
      return getTierTopupUsed(tier);
    }
  } else if (
    tier.subscriptionConsumedCredits != null
    || tier.subscription_consumed_credits != null
  ) {
    return getTierSubscriptionUsed(tier);
  }

  return getTierTotalUsed(tier) || parseCreditAmount(tier.used ?? 0);
};

const getTierPlanUsageTotal = (tier, poolType = "subscription") => {
  if (poolType === "topup") {
    if (
      tier.remainingTopUpCredits != null
      || tier.remaining_top_up_credits != null
      || tier.topupConsumedCredits != null
      || tier.topup_consumed_credits != null
    ) {
      return getTierRemainingTopup(tier) + getTierTopupUsed(tier);
    }
  } else if (
    tier.remainingSubscriptionCredits != null
    || tier.remaining_subscription_credits != null
    || tier.subscriptionConsumedCredits != null
    || tier.subscription_consumed_credits != null
  ) {
    return getTierRemainingSubscription(tier) + getTierSubscriptionUsed(tier);
  }

  return parseCreditAmount(tier.totalCredits ?? tier.total ?? 0);
};

/**
 * Builds PlanTestUsage / UsageProgress data from mapped categoryCredits.
 * Used is subscription_consumed_credits or topup_consumed_credits.
 * Total is remaining plus consumed for that pool.
 */
export const mapCategoryCreditsToPlanUsage = (
  categories = [],
  categoryColors = {},
  {
    poolType = "subscription",
  } = {},
) => {
  const items = categories.map((category) => {
    const value = (category.tiers || []).reduce(
      (sum, tier) => sum + getTierPlanUsageUsed(tier, poolType),
      0,
    );

    const colors = categoryColors[category.key];
    const legendColor = typeof colors === "object" ? colors.legend : colors;
    const segmentBg = typeof colors === "object" ? colors.segment : undefined;

    return {
      key: category.key,
      title: category.name,
      shortTitle: PLAN_USAGE_SHORT_TITLES[category.key] || category.name,
      value,
      bgcolor: legendColor,
      segmentBg,
    };
  });

  const total = categories.reduce((sum, category) => (
    sum + (category.tiers || []).reduce(
      (tierSum, tier) => tierSum + getTierPlanUsageTotal(tier, poolType),
      0,
    )
  ), 0);

  const used = items.reduce((sum, item) => sum + Number(item.value || 0), 0);

  return {
    items,
    used,
    total,
    exhausted: total > 0 && used >= total,
  };
};

export const getTierStatus = (used, total) => {
  if (total <= 0) return "critical";
  const remaining = total - used;
  const remainingRatio = remaining / total;
  if (remainingRatio <= 0.15) return "critical";
  if (remainingRatio <= 0.4) return "warning";
  return "healthy";
};

export const getTierStatusLabel = (status) => {
  if (status === "critical") return "Critical";
  if (status === "warning") return "Low";
  return "Healthy";
};

export const getUsageStatusLabel = (status, used = 0, total = 0) => {
  if (status === "critical") return "Exhausted";
  if (status === "warning") {
    const percent = total > 0 ? (used / total) * 100 : 0;
    const formatted = Number.isInteger(percent)
      ? percent
      : percent.toFixed(1);
    return `Running low: ${formatted}% Exhausted`;
  }
  return "Healthy";
};

export const getUsageTierStatus = (used, total) => {
  if (used <= 0 && total <= 0) return "critical";
  if (used <= 0) return "healthy";
  if (total <= 0 || used >= total) return "critical";

  const usedPercent = (used / total) * 100;
  if (usedPercent >= 90) return "warning";

  return "healthy";
};

export const SUBSCRIPTION_TOP_UP_USAGE_THRESHOLD = 0.9;

/**
 * True when every subscription category with an allocated pool has used
 * at least 90% of its subscription total (remaining + consumed).
 * @param {Array} categories
 * @param {number} [threshold]
 * @returns {boolean}
 */
export const hasSubscriptionUsageReachedTopUpThreshold = (
  categories = [],
  threshold = SUBSCRIPTION_TOP_UP_USAGE_THRESHOLD,
) => {
  const categoryUsages = (categories || [])
    .map((category) => (
      (category.tiers || []).reduce((totals, tier) => {
        const usage = getSubscriptionTierUsage(tier, {
          billingMode: "subscription",
          topupPlan: false,
        });
        return {
          used: totals.used + usage.used,
          total: totals.total + usage.total,
        };
      }, { used: 0, total: 0 })
    ))
    .filter((usage) => usage.total > 0);

  if (!categoryUsages.length) {
    return false;
  }

  return categoryUsages.every((usage) => (usage.used / usage.total) >= threshold);
};

/**
 * True when every subscription category with an allocated pool is fully used.
 * @param {Array} categories
 * @returns {boolean}
 */
export const areSubscriptionCreditsExhausted = (categories = []) => (
  hasSubscriptionUsageReachedTopUpThreshold(categories, 1)
);

export const getStartTopupPoolAlertMonthKey = (date = moment()) => (
  moment(date).format("YYYY-MM")
);

export const hasStartTopupPoolAlertBeenShownThisMonth = (
  monthKey = getStartTopupPoolAlertMonthKey(),
) => {
  try {
    return localStorage.getItem(START_TOPUP_POOL_ALERT_SHOWN_KEY) === monthKey;
  } catch (error) {
    return false;
  }
};

export const markStartTopupPoolAlertShownThisMonth = (
  monthKey = getStartTopupPoolAlertMonthKey(),
) => {
  try {
    localStorage.setItem(START_TOPUP_POOL_ALERT_SHOWN_KEY, monthKey);
    localStorage.removeItem(START_TOPUP_POOL_ALERT_PENDING_KEY);
  } catch (error) {
    return undefined;
  }
  return undefined;
};

export const queueStartTopupPoolAlert = ({
  monthKey = getStartTopupPoolAlertMonthKey(),
  poolTests = 0,
} = {}) => {
  if (hasStartTopupPoolAlertBeenShownThisMonth(monthKey)) {
    return false;
  }

  try {
    localStorage.setItem(
      START_TOPUP_POOL_ALERT_PENDING_KEY,
      JSON.stringify({
        monthKey,
        poolTests: Number(poolTests) || 0,
      }),
    );
    return true;
  } catch (error) {
    return false;
  }
};

export const getStartTopupPoolAlertPending = (
  monthKey = getStartTopupPoolAlertMonthKey(),
) => {
  try {
    const raw = localStorage.getItem(START_TOPUP_POOL_ALERT_PENDING_KEY);
    if (raw == null) return null;

    const parsed = JSON.parse(raw);
    if (!parsed || parsed.monthKey !== monthKey) {
      return null;
    }

    return parsed;
  } catch (error) {
    return null;
  }
};

export const getUsageProgressColor = (used, total) => {
  const status = getUsageTierStatus(used, total);

  const USAGE_PROGRESS_COLORS = {
    healthy: designTokens.colorUsageHealthyProgress,
    warning: designTokens.colorUsageWarningProgress,
    critical: designTokens.colorUsageCriticalProgress,
  };

  return USAGE_PROGRESS_COLORS[status] || USAGE_PROGRESS_COLORS.healthy;
};

export const buildInitialQuantities = (categories) => (
  categories.reduce((quantities, category) => {
    category.tests.forEach((test) => {
      quantities[test.key] = test.initialQuantity;
    });
    return quantities;
  }, {})
);

const normalizePrefillCredits = (credits) => {
  if (Array.isArray(credits)) {
    return credits
      .map((credit) => ({
        usage_category_code: String(credit.usage_category_code).toUpperCase(),
        quantity: Number(credit.quantity),
      }))
      .filter((credit) => credit.usage_category_code && credit.quantity > 0);
  }

  if (!credits || typeof credits !== "object") {
    return [];
  }

  return Object.entries(credits)
    .map(([code, quantity]) => ({
      usage_category_code: String(code).toUpperCase(),
      quantity: Number(quantity),
    }))
    .filter((credit) => credit.usage_category_code && credit.quantity > 0);
};

export const setPlanBuilderPrefillCredits = (credits) => {
  try {
    localStorage.setItem(
      PLAN_BUILDER_PREFILL_CREDITS_KEY,
      JSON.stringify(normalizePrefillCredits(credits)),
    );
  } catch (error) {
    return undefined;
  }
  return undefined;
};

export const getPlanBuilderPrefillCredits = () => {
  try {
    const raw = localStorage.getItem(PLAN_BUILDER_PREFILL_CREDITS_KEY);
    if (raw == null) return [];
    return normalizePrefillCredits(JSON.parse(raw));
  } catch (error) {
    return [];
  }
};

export const clearPlanBuilderPrefillCredits = () => {
  try {
    localStorage.removeItem(PLAN_BUILDER_PREFILL_CREDITS_KEY);
  } catch (error) {
    return undefined;
  }
  return undefined;
};

export const applyPrefillCreditsToQuantities = (categories, credits) => {
  const quantities = buildInitialQuantities(categories);
  const creditsByCode = normalizePrefillCredits(credits).reduce((acc, credit) => {
    acc[credit.usage_category_code] = credit.quantity;
    return acc;
  }, {});

  categories.forEach((category) => {
    (category.tests || []).forEach((test) => {
      const code = String(test.code || test.key || "").toUpperCase();
      const requested = creditsByCode[code];
      if (requested == null || requested <= 0) return;
      const maxQuantity = Number(test.maximumAvailability);
      if (!Number.isFinite(maxQuantity) || maxQuantity <= 0) {
        quantities[test.key] = 0;
        return;
      }
      quantities[test.key] = Math.min(requested, maxQuantity);
    });
  });

  return quantities;
};

/**
 * Calculate plan-builder payment total from selected quantities.
 * Payment = sum(quantity × unit cost) for each usage category.
 * @param {Object.<string, number>} quantities
 * @param {Array} categories
 * @returns {{ total: number, totalCredits: number }}
 */
export const calculatePlanBuilderTotals = (quantities = {}, categories = []) => (
  categories.reduce((summary, category) => {
    category.tests.forEach((test) => {
      const quantity = Number(quantities[test.key]);
      if (!Number.isFinite(quantity)) return;
      summary.total += quantity * Number(test.unitPrice);
      summary.totalCredits += quantity;
    });
    return summary;
  }, { total: 0, totalCredits: 0 })
);

/**
 * Build POST /usage/recharge/ credits payload from plan-builder quantities.
 * Only includes rows with quantity > 0 and a usage_category_code.
 * @param {Object.<string, number>} quantities
 * @param {Array} [categories]
 * @returns {Array<{ usage_category_code: string, quantity: number }>}
 */
const REVIEW_ITEM_KEY_TO_CODE = Object.entries(USAGE_CREDITS_CATEGORY_MAP).reduce(
  (acc, [code, mapping]) => {
    acc[`${mapping.categoryKey}-${mapping.tierName.toLowerCase()}`] = code;
    return acc;
  },
  {},
);

export const buildCheckoutCreditsFromReviewCategories = (categories = []) => {
  const credits = [];

  categories.forEach((category) => {
    (category.items || []).forEach((item) => {
      const code = item.code || REVIEW_ITEM_KEY_TO_CODE[item.key];
      const quantity = Number(item.quantity) || 0;

      if (code && quantity > 0) {
        credits.push({
          usage_category_code: code,
          quantity,
        });
      }
    });
  });

  return credits;
};

export const buildRechargeCredits = (
  quantities = {},
  categories = [],
) => {
  const credits = [];

  categories.forEach((category) => {
    (category.tests || []).forEach((test) => {
      const quantity = Number(quantities[test.key]) || 0;
      if (quantity > 0 && test.code) {
        credits.push({
          usage_category_code: test.code,
          quantity,
        });
      }
    });
  });

  return credits;
};

const CATEGORY_PREFIX_MAP = {
  MICRO: "microbiology",
  CHEM: "chemistry",
  CHEMISTRY: "chemistry",
};

const TIER_SUFFIX_MAP = {
  BASIC: "Basic",
  ADVANCED: "Advanced",
  SPECIALTY: "Specialty",
};

/**
 * Resolve API usage category code to UI category + tier.
 * Accepts credits rows (`usage_category_code`) or usage-categories rows (`code`).
 * @param {{
 *   usage_category_code?: string,
 *   code?: string,
 * }} credit
 * @returns {{ categoryKey: string, tierName: string } | null}
 */
export const resolveUsageCategory = (credit = {}) => {
  const code = String(credit.usage_category_code || credit.code).toUpperCase().trim();
  if (USAGE_CREDITS_CATEGORY_MAP[code]) {
    const { categoryKey, tierName } = USAGE_CREDITS_CATEGORY_MAP[code];
    return { categoryKey, tierName };
  }

  if (!code) return null;

  const parts = code.split("_").filter(Boolean);
  if (parts.length >= 2) {
    const tierName = TIER_SUFFIX_MAP[parts[parts.length - 1]];
    const categoryKey = CATEGORY_PREFIX_MAP[parts.slice(0, -1).join("_")]
      || CATEGORY_PREFIX_MAP[parts[0]];
    if (categoryKey && tierName) {
      return { categoryKey, tierName };
    }
  }

  return null;
};

/**
 * Per-category quantity cap from GET /usage-categories/ maximum_availability.
 * @param {number|string|null|undefined} value
 * @returns {number}
 */
export const resolvePlanBuilderMaxQuantity = (value) => Number(value);

/**
 * Map GET /usage-categories/ result into plan-builder category groups.
 * @param {Array} results
 * @returns {Array}
 */
export const mapUsageCategoriesToPlanBuilder = (results) => {
  const categoriesByKey = {};

  Object.values(USAGE_CREDITS_CATEGORY_META).forEach((meta) => {
    categoriesByKey[meta.key] = {
      key: meta.key,
      name: meta.name,
      accentClass: `UsageMetricsPlanBuilder__CategoryIcon--${meta.key}`,
      tests: [],
    };
  });

  (results)
    .filter((item) => Number(item.status) === 1)
    .forEach((item) => {
      const resolved = resolveUsageCategory(item);
      if (!resolved) return;

      const category = categoriesByKey[resolved.categoryKey];
      if (!category) return;

      category.tests.push({
        key: item.code,
        name: resolved.tierName,
        code: item.code,
        unitPrice: parseFloat(item.cost),
        initialQuantity: 0,
        maximumAvailability: resolvePlanBuilderMaxQuantity(item.maximum_availability),
      });
    });

  return Object.values(USAGE_CREDITS_CATEGORY_META)
    .map((meta) => categoriesByKey[meta.key])
    .filter((category) => category.tests.length > 0)
    .map((category) => {
      const tierOrder = USAGE_CREDITS_CATEGORY_META[category.key].tierOrder;

      return {
        ...category,
        tests: [...category.tests].sort(
          (a, b) => tierOrder.indexOf(a.name) - tierOrder.indexOf(b.name),
        ),
      };
    });
};

/**
 * Parse remaining credits from result.total_credits.
 * @param {string|number|null|undefined} totalCredits
 * @returns {number}
 */
export const getTotalCreditsBalance = (totalCredits) => (
  parseCreditAmount(totalCredits)
);

/**
 * Sum topup_consumed_credits across credits rows or mapped category usage.
 * @param {Array} creditsOrCategories
 * @returns {number}
 */
export const getTotalTopupConsumedCredits = (creditsOrCategories = []) => (
  creditsOrCategories.reduce((sum, item) => {
    if (Array.isArray(item.tiers)) {
      return sum + item.tiers.reduce(
        (tierSum, tier) => tierSum + getTierTopupUsed(tier),
        0,
      );
    }
    return sum + parseCreditAmount(item.topup_consumed_credits);
  }, 0)
);

/**
 * Total top-up credits still available (remaining_top_up_credits).
 * @param {Array} creditsOrCategories
 * @returns {number}
 */
export const getTotalTopUpAvailableCredits = (creditsOrCategories = []) => {
  const total = creditsOrCategories.reduce((sum, item) => {
    if (Array.isArray(item.tiers)) {
      return sum + item.tiers.reduce(
        (tierSum, tier) => tierSum + getTierRemainingTopup(tier),
        0,
      );
    }

    return sum + parseCreditAmount(item.remaining_top_up_credits);
  }, 0);

  return Math.max(total, 0);
};

/**
 * Merge /usage/credits/ rows into prepaid category cards for the homepage.
 * Only categories and tiers present in the response are returned.
 * Uses remaining_top_up_credits as remaining and topup_consumed_credits as used.
 * @param {Array} credits
 * @returns {Array}
 */
export const mapCreditsToCategoryUsage = (credits = []) => {
  const tiersByCategory = {};
  const testsLabelByCategory = {};

  (Array.isArray(credits) ? credits : []).forEach((credit) => {
    const resolved = resolveUsageCategory(credit);
    if (!resolved) return;

    const meta = USAGE_CREDITS_CATEGORY_META[resolved.categoryKey];
    if (!meta) return;

    if (!tiersByCategory[resolved.categoryKey]) {
      tiersByCategory[resolved.categoryKey] = {};
    }

    const tierFields = mapCreditRowToTierFields(credit);
    const tier = {
      name: resolved.tierName,
      code: credit.usage_category_code,
      ...tierFields,
    };
    const topupUsed = getTierTopupUsed(tier);
    const topupRemaining = getTierRemainingTopup(tier);
    tier.totalCredits = parseCreditAmount(credit.total_credits);
    tier.used = topupUsed;
    tier.total = topupUsed + topupRemaining;
    tiersByCategory[resolved.categoryKey][resolved.tierName] = tier;

    const testsLabel = getTestsLabelFromCredit(credit);
    if (testsLabel && !testsLabelByCategory[resolved.categoryKey]) {
      testsLabelByCategory[resolved.categoryKey] = testsLabel;
    }
  });

  return Object.values(USAGE_CREDITS_CATEGORY_META)
    .map((meta) => {
      const categoryTiers = tiersByCategory[meta.key] || {};
      const tiers = (meta.tierOrder || [])
        .map((tierName) => categoryTiers[tierName])
        .filter(Boolean);

      if (!tiers.length) return null;

      return {
        key: meta.key,
        name: meta.name,
        ...(testsLabelByCategory[meta.key]
          ? { testsLabel: testsLabelByCategory[meta.key] }
          : {}),
        tiers,
      };
    })
    .filter(Boolean);
};

/**
 * Build remaining-credit lookup by usage category code from homepage credits data.
 * @param {Array} categoryUsage
 * @param {{ billingMode?: string, topupPlan?: boolean }} [options]
 * @returns {Object.<string, number>}
 */
export const buildRemainingCreditsByCode = (
  categoryUsage = [],
  {
    billingMode = "prepaid",
    topupPlan = false,
  } = {},
) => {
  const remainingByCode = {};

  categoryUsage.forEach((category) => {
    (category.tiers || []).forEach((tier) => {
      if (!tier.code) return;
      const { remaining } = getSubscriptionTierUsage(tier, { billingMode, topupPlan });
      remainingByCode[String(tier.code).toUpperCase()] = remaining;
    });
  });

  return remainingByCode;
};

/**
 * Map /usage-categories/tests/ groups into View tests UI categories.
 * Only includes tests with status === 1.
 * @param {Array} groups
 * @param {Object.<string, number>} [remainingByCode]
 * @param {{ iconClassPrefix?: string }} [options]
 * @returns {Array}
 */
export const mapUsageCategoryTestsToModal = (
  groups,
  remainingByCode = {},
  { iconClassPrefix = "UsageMetricsTestsModal__CategoryIcon" } = {},
) => {
  const categories = Object.values(USAGE_CREDITS_CATEGORY_META).map((meta) => ({
    key: meta.key,
    name: meta.name,
    iconClass: `${iconClassPrefix}--${meta.key}`,
    tests: [],
    tierTotals: {},
  }));

  const categoryByKey = Object.fromEntries(
    categories.map((category) => [category.key, category]),
  );

  groups.forEach((group) => {
    const resolved = resolveUsageCategory(group);
    if (!resolved) return;

    const category = categoryByKey[resolved.categoryKey];
    if (!category) return;

    const code = String(group.usage_category_code).toUpperCase();
    const remaining = remainingByCode[code];
    category.tierTotals[resolved.tierName] = remaining;

    group.tests.forEach((test) => {
      if (Number(test.status) !== 1) return;

      category.tests.push({
        id: test.id,
        name: test.display_name,
        tier: resolved.tierName,
        code,
        used: test.used != null ? Number(test.used) : null,
        total: remaining,
        status: test.status,
      });
    });
  });

  return categories.filter((category) => category.tests.length > 0);
};

/**
 * Build tier filter chips with live counts from mapped modal categories.
 * @param {Array} categories
 * @returns {Array<{ key: string, label: string }>}
 */
export const buildTestFilters = (categories = []) => {
  const counts = {
    All: 0,
    Basic: 0,
    Advanced: 0,
    Specialty: 0,
  };

  categories.forEach((category) => {
    (category.tests || []).forEach((test) => {
      counts.All += 1;
      if (counts[test.tier] != null) {
        counts[test.tier] += 1;
      }
    });
  });

  return [
    { key: "All", label: `All (${counts.All})` },
    { key: "Basic", label: `Basic (${counts.Basic})` },
    { key: "Advanced", label: `Advanced (${counts.Advanced})` },
    { key: "Specialty", label: `Specialty (${counts.Specialty})` },
  ];
};

const formatOutstandingDate = (value) => {
  if (value == null || value === "") {
    return undefined;
  }

  const parsed = moment(value);
  return parsed.isValid() ? parsed.format("MMM D, YYYY") : String(value);
};

const formatOutstandingDateRange = (start, end) => {
  const startFormatted = formatOutstandingDate(start);
  const endFormatted = formatOutstandingDate(end);

  if (startFormatted && endFormatted) {
    return `${startFormatted} - ${endFormatted}`;
  }

  return startFormatted || endFormatted;
};

const parseOutstandingAmount = (value) => {
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : 0;
};

const capitalizeBillingCycle = (value) => {
  const normalized = String(value || "").trim().toLowerCase();
  if (!normalized) {
    return undefined;
  }

  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

const formatTermLabelFromMonths = (termMonths) => {
  const months = Number(termMonths);
  if (!Number.isFinite(months) || months <= 0) {
    return undefined;
  }

  if (months % 12 === 0) {
    const years = months / 12;
    return `${years}-Year Term`;
  }

  return `${months}-Month Term`;
};

const formatTermLengthFromMonths = (termMonths) => {
  const months = Number(termMonths);
  if (!Number.isFinite(months) || months <= 0) {
    return undefined;
  }

  return `${months} month${months === 1 ? "" : "s"}`;
};

const formatSpendPeriodLabel = (value) => {
  if (value == null || value === "") {
    return undefined;
  }

  const parsed = moment(value);
  return parsed.isValid() ? parsed.format("MMM YYYY") : undefined;
};

/**
 * Map /usage/outstanding/ result into SubscriptionSummaryCards props.
 * @param {object} [result]
 * @returns {object}
 */
export const mapUsageOutstandingToSummaryCards = (result = {}) => {
  const mapped = {};

  if (result.outstanding != null && result.outstanding !== "") {
    mapped.payableAmount = parseOutstandingAmount(result.outstanding);
  }

  if (result.billing_amount != null && result.billing_amount !== "") {
    mapped.monthlyBillingAmount = parseOutstandingAmount(result.billing_amount);
  }

  if (result.billing_cycle != null && result.billing_cycle !== "") {
    mapped.billingCycle = capitalizeBillingCycle(result.billing_cycle);
  }

  if (result.plan_type != null && result.plan_type !== "") {
    mapped.planType = String(result.plan_type);
  }

  if (result.subscription_mode != null && result.subscription_mode !== "") {
    mapped.subscriptionMode = String(result.subscription_mode).trim().toLowerCase();
  }

  if (result.term != null && result.term !== "") {
    mapped.termLabel = formatTermLabelFromMonths(result.term);
    mapped.termLength = formatTermLengthFromMonths(result.term);
  }

  const termDateRange = formatOutstandingDateRange(
    result.current_period_start,
    result.current_period_end,
  );
  if (termDateRange) {
    mapped.termDateRange = termDateRange;
  }

  const nextBillingMoment = moment(result.next_billing_date);
  if (nextBillingMoment.isValid()) {
    const dueMoment = nextBillingMoment.clone().add(1, "day");
    mapped.dueDate = dueMoment.format("MMM D, YYYY");
    mapped.renewsOn = mapped.dueDate;
    mapped.cycleResetDays = Math.max(
      dueMoment.startOf("day").diff(moment().startOf("day"), "days"),
      0,
    );
  }

  const spendPeriodLabel = formatSpendPeriodLabel(result.current_period_end);
  if (spendPeriodLabel) {
    mapped.spendPeriodLabel = spendPeriodLabel;
  }

  const periodStart = moment(result.current_period_start);
  const periodEnd = moment(result.current_period_end);
  if (periodStart.isValid()) {
    mapped.spendPeriodStart = periodStart.format("YYYY-MM-DD");
  }
  if (periodEnd.isValid()) {
    mapped.spendPeriodEnd = periodEnd.format("YYYY-MM-DD");
  }

  return mapped;
};

const TREND_SERIES_COLOR_TOKENS = [
  "colorAnalyticsRust",
  "colorAnalyticsPink",
  "colorAnalyticsPurple",
];

const LOCATION_SERIES_COLOR_TOKENS = [
  "colorAnalyticsPurple",
  "colorAnalyticsRust",
  "colorAnalyticsTealGreen",
  "colorAnalyticsPink",
  "colorAnalyticsBlue",
  "colorAnalyticsSage",
];

/** Aggregate tier keys into the chart categories returned by the API. */
const CREDIT_CONSUMPTION_MAIN_CATEGORIES = [
  {
    key: "microbiology",
    label: "Microbiology",
    match: (seriesKey) => /^micro_/i.test(seriesKey),
  },
  {
    key: "chemistry",
    label: "Chemistry",
    match: (seriesKey) => /^chem_/i.test(seriesKey),
  },
];

const sumMatchedValues = (values, matchFn) => (
  Object.entries(values).reduce((total, [key, amount]) => (
    matchFn(key) ? total + Number(amount) : total
  ), 0)
);

const collectCreditConsumptionSeriesKeys = (response = {}) => {
  const keys = new Set();

  (Array.isArray(response.series) ? response.series : []).forEach((item) => {
    const key = typeof item === "string" ? item : item?.key;
    if (key) keys.add(String(key));
  });

  (Array.isArray(response.data) ? response.data : []).forEach((point) => {
    Object.keys(point?.values || {}).forEach((key) => keys.add(key));
  });

  return keys;
};

const readCreditConsumptionSeriesLabel = (response = {}, key) => {
  const match = (Array.isArray(response.series) ? response.series : []).find((item) => (
    item && typeof item === "object" && String(item.key) === key
  ));
  return String(match?.label || key).trim() || key;
};

/**
 * Maps /usage/credit-consumption-graph/ response into Recharts series + points.
 * Known tier keys (e.g. micro_basic) roll up into Microbiology or Chemistry
 * only when the response includes that category. Other series keys are
 * charted with the API label.
 *
 * @param {{ data?: Array, series?: Array }} response
 * @returns {{ data: Array, series: Array }}
 */
export const mapCreditConsumptionGraphToTrend = (response = {}) => {
  const points = Array.isArray(response.data) ? response.data : [];
  const presentKeys = [...collectCreditConsumptionSeriesKeys(response)];
  const matchedCategories = CREDIT_CONSUMPTION_MAIN_CATEGORIES
    .map((category, index) => ({ category, index }))
    .filter(({ category }) => presentKeys.some((key) => category.match(key)));

  const series = matchedCategories.map(({ category, index }) => ({
    key: category.key,
    label: category.label,
    colorToken: TREND_SERIES_COLOR_TOKENS[index % TREND_SERIES_COLOR_TOKENS.length],
  }));

  presentKeys
    .filter((key) => !CREDIT_CONSUMPTION_MAIN_CATEGORIES.some((category) => category.match(key)))
    .forEach((key) => {
      series.push({
        key,
        label: readCreditConsumptionSeriesLabel(response, key),
        colorToken: TREND_SERIES_COLOR_TOKENS[series.length % TREND_SERIES_COLOR_TOKENS.length],
      });
    });

  const data = points.map((point) => {
    const row = { week: point.period };
    const values = point?.values && typeof point.values === "object" ? point.values : {};

    matchedCategories.forEach(({ category }) => {
      row[category.key] = sumMatchedValues(values, category.match);
    });

    series.forEach((item) => {
      if (row[item.key] != null) return;
      row[item.key] = Number(values[item.key]) || 0;
    });

    return row;
  });

  return { data, series };
};

/** Payment-method series for credit consumption (subscription vs top-up). */
const CREDIT_CONSUMPTION_PAYMENT_METHODS = [
  {
    key: "subscription",
    label: "Subscription Allotment",
    aliases: ["subscription"],
  },
  {
    key: "top_up",
    label: "Top-up Used",
    aliases: ["top_up", "topup", "top-up"],
  },
];

const normalizePaymentMethodKey = (value) => (
  String(value || "").trim().toLowerCase().replace(/-/g, "_")
);

const resolvePaymentMethodMeta = (key) => {
  const normalized = normalizePaymentMethodKey(key);
  return CREDIT_CONSUMPTION_PAYMENT_METHODS.find((method) => (
    method.aliases.includes(normalized)
  ));
};

/**
 * Maps /usage/credit-consumption-graph/?group_by=payment_method into Recharts
 * series + points. Each cycle shows credits consumed from subscription vs top-up.
 *
 * @param {{ data?: Array, series?: Array }} response
 * @returns {{ data: Array, series: Array }}
 */
export const mapCreditConsumptionGraphByPaymentMethod = (response) => {
  const points = Array.isArray(response?.data) ? response.data : [];
  const apiSeries = Array.isArray(response?.series) ? response.series : [];
  const fallbackSeries = CREDIT_CONSUMPTION_PAYMENT_METHODS.map(({ key, label }) => ({
    key,
    label,
  }));
  const seriesSource = apiSeries.length ? apiSeries : fallbackSeries;
  const seen = new Set();

  const series = seriesSource.reduce((items, item) => {
    const rawKey = typeof item === "string" ? item : item?.key;
    const meta = resolvePaymentMethodMeta(rawKey);
    const key = meta?.key || normalizePaymentMethodKey(rawKey);
    if (!key || seen.has(key)) {
      return items;
    }

    seen.add(key);
    items.push({
      key,
      label: meta?.label || String(item?.label || rawKey || key).trim(),
      colorToken: LOCATION_SERIES_COLOR_TOKENS[items.length % LOCATION_SERIES_COLOR_TOKENS.length],
    });
    return items;
  }, []);

  const data = points.map((point) => {
    const row = { week: point.period };
    const values = point?.values && typeof point.values === "object" ? point.values : {};

    series.forEach((item) => {
      const matchedKey = Object.keys(values).find((valueKey) => (
        normalizePaymentMethodKey(valueKey) === item.key
        || resolvePaymentMethodMeta(valueKey)?.key === item.key
      ));
      row[item.key] = Number(matchedKey != null ? values[matchedKey] : 0) || 0;
    });

    return row;
  });

  return { data, series };
};

const toNumericAmount = (value) => {
  if (value == null || value === "") return 0;
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  if (typeof value === "string") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  if (typeof value === "object") {
    return Object.values(value).reduce((total, item) => total + toNumericAmount(item), 0);
  }
  return 0;
};

const readLocationSeriesLabel = (item) => {
  if (typeof item === "string") return item.trim();
  if (!item || typeof item !== "object") return "";
  return String(item.label || item.name || item.location || item.key || "").trim();
};

/**
 * Maps a group_by=location credit-consumption-graph response into chart series.
 * Each location in series or point.values becomes one line.
 *
 * @param {{ data?: Array, series?: Array }} response
 * @returns {{ data: Array, series: Array }}
 */
export const mapCreditConsumptionGraphByLocation = (response) => {
  const points = Array.isArray(response?.data) ? response.data : [];
  const series = [];
  const seen = new Set();

  const addSeries = (lookupKeys, label) => {
    const keys = lookupKeys.map((key) => String(key || "").trim()).filter(Boolean);
    const name = String(label || keys[0] || "").trim();
    if (!name || seen.has(name) || keys.some((key) => seen.has(key))) return;

    seen.add(name);
    keys.forEach((key) => seen.add(key));
    series.push({
      key: name,
      label: name,
      lookupKeys: keys.length ? keys : [name],
      colorToken: LOCATION_SERIES_COLOR_TOKENS[series.length % LOCATION_SERIES_COLOR_TOKENS.length],
    });
  };

  (Array.isArray(response?.series) ? response.series : []).forEach((item) => {
    if (typeof item === "string") {
      addSeries([item], item);
      return;
    }
    const label = readLocationSeriesLabel(item);
    addSeries([item?.key, item?.location, label], label);
  });

  points.forEach((point) => {
    const values = point?.values && typeof point.values === "object" ? point.values : {};
    Object.keys(values).forEach((key) => addSeries([key], key));
  });

  const data = points.map((point) => {
    const row = { week: point.period };
    const values = point?.values && typeof point.values === "object" ? point.values : {};
    series.forEach((item) => {
      const raw = item.lookupKeys.map((key) => values[key]).find((value) => value != null);
      row[item.key] = toNumericAmount(raw);
    });
    return row;
  });

  return {
    data,
    series: series.map(({ key, label, colorToken }) => ({ key, label, colorToken })),
  };
};

const NOT_ENOUGH_USAGE_DATA = "Not enough data";

const parseOptionalNumber = (value) => {
  if (value == null || value === "") return null;
  const parsed = Number(String(value).replace(/%/g, "").trim());
  return Number.isFinite(parsed) ? parsed : null;
};

const formatWholeNumber = (value) => (
  Number(value).toLocaleString(undefined, { maximumFractionDigits: 0 })
);

const formatPercentAmount = (value) => {
  const absolute = Math.abs(value);
  const formatted = Number.isInteger(absolute)
    ? String(absolute)
    : absolute.toLocaleString(undefined, { maximumFractionDigits: 1 });
  return `${formatted}%`;
};

const formatSignedPercent = (value) => {
  if (value > 0) return `+${formatPercentAmount(value)}`;
  if (value < 0) return `-${formatPercentAmount(value)}`;
  return formatPercentAmount(value);
};

const formatTestCount = (value) => {
  const amount = formatWholeNumber(value);
  const unit = Math.abs(Number(value)) === 1 ? "test" : "tests";
  return `${amount} ${unit}`;
};

const formatRemainingDays = (value) => {
  const amount = formatWholeNumber(value);
  const unit = Math.abs(Number(value)) === 1 ? "day" : "days";
  return `${amount} ${unit}`;
};

/**
 * Equal-length window immediately before the selected usage range.
 * May 15 - Jun 14 becomes Apr 14 - May 14.
 * @param {string} startDate
 * @param {string} endDate
 * @returns {string}
 */
const formatPreviousPeriodRange = (startDate, endDate) => {
  const rangeStart = moment(startDate, "YYYY-MM-DD", true);
  const rangeEnd = moment(endDate, "YYYY-MM-DD", true);
  if (!rangeStart.isValid() || !rangeEnd.isValid()) return "";

  const daySpan = rangeEnd.diff(rangeStart, "days");
  const previousEnd = rangeStart.clone().subtract(1, "day");
  const previousStart = previousEnd.clone().subtract(daySpan, "days");
  const crossesYear = previousStart.year() !== previousEnd.year()
    || previousEnd.year() !== rangeEnd.year();
  const dateFormat = crossesYear ? "MMM D, YYYY" : "MMM D";

  return `${previousStart.format(dateFormat)} - ${previousEnd.format(dateFormat)}`;
};

const buildTotalTestsInsight = (response, startDate, endDate) => {
  const totalTests = parseOptionalNumber(response.total_number_tests);
  const testGrowth = parseOptionalNumber(response.test_growth);
  const previousPeriod = formatPreviousPeriodRange(startDate, endDate);
  let detailTone = "muted";
  if (testGrowth > 0) detailTone = "positive";
  if (testGrowth < 0) detailTone = "negative";

  const growthDetail = testGrowth == null
    ? ""
    : [formatSignedPercent(testGrowth), previousPeriod ? `vs ${previousPeriod}` : ""]
      .filter(Boolean)
      .join(" ");

  return {
    key: "total-tests",
    icon: "flask",
    label: "Total tests",
    count: totalTests,
    value: totalTests == null ? NOT_ENOUGH_USAGE_DATA : formatTestCount(totalTests),
    detailHighlight: "",
    detail: growthDetail,
    detailTone,
  };
};

const buildHighestLocationInsight = (response) => {
  const location = response.highest_consumption_location;
  const locationName = String(location?.location_name || "").trim();
  const usagePercent = parseOptionalNumber(location?.percent);

  return {
    key: "highest-location",
    icon: "pin",
    label: "Highest consuming location",
    value: locationName || NOT_ENOUGH_USAGE_DATA,
    detailHighlight: usagePercent == null ? "" : formatPercentAmount(usagePercent),
    detail: usagePercent == null ? "" : "of total usage",
    detailTone: "muted",
  };
};

const buildPoolRunoutInsight = (response) => {
  const remainingDays = parseOptionalNumber(response.remaining_days);
  const hasProjection = remainingDays != null;

  return {
    key: "projected-runout",
    icon: "history",
    label: "Projected pool runout",
    value: hasProjection ? formatRemainingDays(remainingDays) : NOT_ENOUGH_USAGE_DATA,
    detailHighlight: "",
    detail: hasProjection ? "Based on current usage trend" : "",
    detailTone: "muted",
  };
};

/**
 * Maps credit-consumption-graph summary fields into the trend insight cards.
 * `remaining_days: null` is shown as not enough data.
 *
 * @param {{
 *   total_number_tests?: number | string | null,
 *   test_growth?: number | string | null,
 *   highest_consumption_location?: { location_name?: string, percent?: number | string | null } | null,
 *   remaining_days?: number | string | null,
 * }} response
 * @param {{ startDate?: string, endDate?: string }} [range]
 * @returns {Array}
 */
export const mapCreditConsumptionInsights = (response = {}, range = {}) => ([
  buildTotalTestsInsight(response, range.startDate, range.endDate),
  buildHighestLocationInsight(response),
  buildPoolRunoutInsight(response),
]);

/**
 * Last calendar month through today (YYYY-MM-DD).
 * @returns {{ startDate: string, endDate: string }}
 */
export const getLastMonthUsageDateRange = () => {
  const end = moment().startOf("day");
  return {
    startDate: end.clone().subtract(1, "month").format("YYYY-MM-DD"),
    endDate: end.format("YYYY-MM-DD"),
  };
};

/**
 * Default YYYY-MM-DD range for the multi-location usage trend chart.
 * @param {number} [days=30]
 * @returns {{ startDate: string, endDate: string }}
 */
export const getDefaultUsageTrendDateRange = (days = 30) => {
  const end = moment().startOf("day");
  const start = end.clone().subtract(Math.max(days - 1, 0), "days");
  return {
    startDate: start.format("YYYY-MM-DD"),
    endDate: end.format("YYYY-MM-DD"),
  };
};
